import type { IconName } from "@scayla/components";
import type {
  LocationQuery,
  RouteLocationAsRelativeGeneric,
  RouteLocationNormalized,
  RouteLocationRaw,
  Router,
} from "vue-router";

function matchesRoute(
  routeToCheck: RouteLocationRaw,
  currentPath: string,
  currentQuery: LocationQuery,
  router: Router,
): boolean {
  const resolved = router.resolve(routeToCheck);
  const samePath = currentPath === resolved.path || currentPath.startsWith(resolved.path + "/");
  // Only the query keys the node itself cares about (e.g. templateId, folderId) need to match —
  // unrelated params the current URL also carries (filterId, sort, page, ...) must not disqualify
  // it, or every such param added elsewhere silently breaks sidebar highlighting everywhere. `tenant`
  // is excluded even though nav-node routes carry it: it's the same value on every node of a module
  // rather than something that distinguishes them, and router.ts's afterEach hook only patches it
  // into the visible URL via history.replaceState, which doesn't update this reactive route.query —
  // so requiring it here intermittently broke highlighting (e.g. on /filter/create).
  const sameQuery = Object.entries(resolved.query)
    .filter(([key]) => key !== "tenant")
    .every(([key, value]) => currentQuery[key] === value);
  return samePath && sameQuery;
}

export interface NavNode {
  label: string;
  icon?: IconName;
  route?: RouteLocationAsRelativeGeneric;
  activeRoutes?: RouteLocationRaw[];
  layoutStoreKey?: string;
  actions?: boolean;
  // Set false on nodes the favorites list can't represent (e.g. the trash bin's modules).
  canFavorite?: boolean;
  children?: NavNode[];
  disabled?: boolean;
  indexId?: number;
  favorite?: boolean;
  onItemClick?: () => void;
}

export interface NavRouteQueryLookup {
  folderId?: string;
  templateId?: string;
}

function getRouteQueryValue(route: RouteLocationAsRelativeGeneric | undefined, key: keyof NavRouteQueryLookup) {
  const value = route?.query?.[key];
  return typeof value === "string" ? value : undefined;
}

export function nodeMatchesSearch(node: NavNode, lowerTerm: string): boolean {
  if (!lowerTerm) return true;
  if (!node) return false;

  const term = lowerTerm.trim().toLowerCase();
  if (!term) return true;

  const tokens = term.split(/\s+/).filter(Boolean);

  const collectLabels = (node: NavNode): string[] => {
    const labels: string[] = [];
    if (node.label) labels.push(node.label);
    if (node.children?.length) {
      for (const child of node.children) labels.push(...collectLabels(child));
    }
    return labels;
  };

  const combined = collectLabels(node).join(" ").toLowerCase();

  // Match when every token appears anywhere in the combined label string
  if (tokens.every((t) => combined.includes(t))) {
    return true;
  }

  if (node.children?.length) {
    return node.children.some((child) => nodeMatchesSearch(child, lowerTerm));
  }

  return false;
}

export function isNodeActive(
  node: NavNode | undefined,
  route: RouteLocationNormalized,
  router: Router,
  deep = true,
): boolean {
  if (!node) return false;

  let matchesSelf = false;

  if (node.activeRoutes) {
    matchesSelf = node.activeRoutes.some((r) => matchesRoute(r, route.path, route.query, router));
  } else {
    if (node.route) {
      matchesSelf = matchesRoute(node.route, route.path, route.query, router);
    }
  }

  if (matchesSelf) return true;

  if (deep && node.children?.length) {
    return node.children.some((child) => isNodeActive(child, route, router, deep));
  }

  return false;
}

export function findNavNodeByRouteQuery(nodes: NavNode[], query: NavRouteQueryLookup): NavNode | undefined {
  const hasLookup = query.folderId !== undefined || query.templateId !== undefined;
  if (!hasLookup) {
    return undefined;
  }

  for (const node of nodes) {
    const folderId = getRouteQueryValue(node.route, "folderId");
    const templateId = getRouteQueryValue(node.route, "templateId");

    const folderMatches = query.folderId === undefined || folderId === query.folderId;
    const templateMatches = query.templateId === undefined || templateId === query.templateId;

    if (folderMatches && templateMatches) {
      return node;
    }

    const childMatch = node.children?.length ? findNavNodeByRouteQuery(node.children, query) : undefined;
    if (childMatch) {
      return childMatch;
    }
  }

  return undefined;
}
